import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Card, Button, Row, Col, Alert } from 'react-bootstrap';
import { FaUsers, FaPlus, FaMinus, FaCreditCard, FaCheck, FaInfoCircle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { ACCOUNT_TYPE_SEATS, SEAT_PRICING, MAX_SEATS, BASE_PLAN_PRICING } from '../constants';
import apiClient from '../services/apiClient';
import TeamNotificationTooltip from '../components/TeamNotificationTooltip';

const Seats = ({ show, onClose, onSeatsUpdate }) => {
  const { user } = useAuth();
  
  const [currentSeats, setCurrentSeats] = useState(0);
  const [targetSeats, setTargetSeats] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [billingData, setBillingData] = useState(null);

  // Map account types to ensure compatibility
  const mapAccountType = (userAccountType) => {
    // Check if we need to get user data from localStorage directly
    let actualAccountType = userAccountType;
    if (!actualAccountType && localStorage.getItem('user')) {
      try {
        const localUser = JSON.parse(localStorage.getItem('user'));
        // Try both account_type and accountType
        actualAccountType = localUser?.account_type || localUser?.accountType;
      } catch (e) {
        console.error('Error parsing localStorage user:', e);
      }
    }
    
    // Also try getting from user object with both property names
    if (!actualAccountType) {
      actualAccountType = user?.account_type || user?.accountType;
    }
    
    const typeMap = {
      'individual': 'solo',
      'Individual': 'solo',
      'Solo': 'solo',
      'solo': 'solo',
      'Solo Assistant': 'solo_assistant',
      'solo_assistant': 'solo_assistant',
      'Small Team': 'small_team',
      'small_team': 'small_team',
      'Large Team': 'large_team',
      'large_team': 'large_team',
      'Brokerage': 'brokerage',
      'brokerage': 'brokerage',
      'broker': 'brokerage', // Map 'broker' to 'brokerage'
      'Super': 'super',
      'super': 'super'
    };
    
    const mappedType = typeMap[actualAccountType] || actualAccountType?.toLowerCase() || 'solo';
    
    return mappedType;
  };

  const accountType = mapAccountType(user?.account_type);
  const defaultSeats = ACCOUNT_TYPE_SEATS[accountType] || 2;
  const maxAllowedSeats = MAX_SEATS[accountType] || 50;
  const baseIncludedSeats = SEAT_PRICING.BASE_PLAN_INCLUDED_SEATS[accountType] || defaultSeats;
  const monthlyCostPerSeat = SEAT_PRICING.MONTHLY_COST_PER_SEAT;

  const loadUserBillingData = useCallback(async () => {
    try {
      const userId = user?.id || user?.userId || user?.user_id;

      console.log('[DEBUG Seats] Fetching billing data for userId:', userId);
      const response = await apiClient.get(`/api/billing/${userId}`);

      console.log('[DEBUG Seats] response.data.success:', response.data.success);
      console.log('[DEBUG Seats] response.data.data:', response.data.data);
      console.log('[DEBUG Seats] currentPlan:', response.data.data?.currentPlan);
      console.log('[DEBUG Seats] seatCount:', response.data.data?.currentPlan?.seatCount);

      if (response.data.success && response.data.data) {
        setBillingData(response.data.data);
        const seats = response.data.data.currentPlan?.seatCount || defaultSeats;
        console.log('[DEBUG Seats] Setting currentSeats to:', seats);
        setCurrentSeats(seats);
        setTargetSeats(seats);
      } else {
        console.log('[DEBUG Seats] No billing data, using defaultSeats:', defaultSeats);
        setCurrentSeats(defaultSeats);
        setTargetSeats(defaultSeats);
      }
    } catch (err) {
      console.error('Failed to load billing data:', err);

      if (err.response?.status === 404) {
        console.log('User not found in billing system, using default seat count');
      } else {
        setError('Failed to load billing data. Using default values.');
      }

      setCurrentSeats(defaultSeats);
      setTargetSeats(defaultSeats);
    }
  }, [user, defaultSeats]);

  useEffect(() => {
    const userId = user?.id || user?.userId || user?.user_id;
    if (userId) {
      loadUserBillingData();
    }
  }, [user, loadUserBillingData]);

  const calculateMonthlyCost = (seats) => {
    const basePlanCost = BASE_PLAN_PRICING[accountType] || BASE_PLAN_PRICING.solo;
    const additionalSeats = Math.max(0, seats - baseIncludedSeats);
    const additionalSeatsCost = additionalSeats * monthlyCostPerSeat;
    return basePlanCost + additionalSeatsCost;
  };

  const handleSeatChange = (newSeats) => {
    if (newSeats >= defaultSeats && newSeats <= maxAllowedSeats) {
      setTargetSeats(newSeats);
    }
  };

  const updateSeats = async () => {
    // Try different possible ID field names
    const userId = user?.id || user?.userId || user?.user_id;
    
    if (!userId) {
      setError('User not logged in');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Create a billing history entry for the seat change
      const newBillingEntry = {
        id: Date.now(),
        date: new Date().toISOString().split('T')[0],
        description: `Seat adjustment: ${currentSeats} → ${targetSeats} seats`,
        seatCount: targetSeats,
        amount: calculateMonthlyCost(targetSeats),
        status: 'Active'
      };

      const updatedBillingHistory = [...(billingData?.billingHistory || []), newBillingEntry];

      const updatedBilling = {
        ...billingData,
        currentPlan: {
          ...billingData?.currentPlan,
          seatCount: targetSeats,
          monthlyRatePerSeat: monthlyCostPerSeat,
          nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        },
        sites: billingData?.sites || [],
        paymentMethod: billingData?.paymentMethod || {
          type: 'card',
          card: { number: '', name: '', expiry: '', cvc: '' }
        },
        billingHistory: updatedBillingHistory
      };

      const response = await apiClient.put(`/api/billing/${userId}`, updatedBilling);

      if (response.data.success) {
        setCurrentSeats(targetSeats);
        setBillingData(updatedBilling);
        setSuccess(`Successfully updated to ${targetSeats} seats!`);
        
        // Notify parent component that seats were updated
        if (onSeatsUpdate) {
          onSeatsUpdate(targetSeats, updatedBilling);
        }
        
        // Also refresh billing data if billing component is open
        if (window.refreshBillingData) {
          window.refreshBillingData();
        }
        
        // Also refresh calendar seats if calendar component is open
        // Add a small delay to ensure the backend has processed the update
        if (window.refreshCalendarSeats) {
          setTimeout(() => {
            window.refreshCalendarSeats();
          }, 100); // 100ms delay
        }
        
        // Dispatch custom event as additional mechanism
        setTimeout(() => {
          const event = new CustomEvent('seatsUpdated', { 
            detail: { 
              newSeatCount: targetSeats, 
              updatedBilling: updatedBilling 
            } 
          });
          window.dispatchEvent(event);
        }, 150); // Slightly later to ensure all other updates complete
        
        // Close the modal after successful update
        setTimeout(() => {
          onClose();
        }, 500); // Small delay to show success message
      } else {
        setError('Failed to update seats. Please try again.');
      }
    } catch (err) {
      console.error('Error updating seats:', err);
      setError('Failed to update seats. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentMonthlyCost = calculateMonthlyCost(currentSeats);
  const targetMonthlyCost = calculateMonthlyCost(targetSeats);
  const costDifference = targetMonthlyCost - currentMonthlyCost;

  // Format account type for display
  const formatAccountTypeForDisplay = (type) => {
    const displayMap = {
      'solo': 'Solo',
      'solo_assistant': 'Solo Assistant',
      'small_team': 'Small Team',
      'large_team': 'Large Team',
      'brokerage': 'Brokerage',
      'super': 'Super'
    };
    
    return displayMap[type] || type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Solo';
  };

  return (
    <Modal 
      show={show} 
      onHide={onClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header style={{
        background: 'linear-gradient(180deg, #eef2f0 0%, #e4ece8 100%)',
        borderBottom: '1px solid #c8d8d2',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.07)',
        borderRadius: 'calc(0.3rem - 1px) calc(0.3rem - 1px) 0 0',
        margin: 0,
        padding: '0 20px',
        minHeight: 42,
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          {accountType && (
            <div style={{
              position: 'absolute', left: 20,
              background: '#dcfce7', color: '#15803d',
              padding: '3px 10px', borderRadius: 999,
              fontSize: 12, fontWeight: 600,
              border: '1px solid #bbf7d0',
              letterSpacing: '0.01em',
            }}>
              {formatAccountTypeForDisplay(accountType)} Plan
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: '#d1fae5', color: '#047857',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16,
            }}><FaUsers /></div>
            <h2 style={{
              margin: 0, color: '#1e3a2f',
              fontSize: 18, fontWeight: 700,
              letterSpacing: '-0.01em',
            }}>
              Manage Seats
              <TeamNotificationTooltip placement="bottom">
                <span style={{ color: '#047857', cursor: 'help', fontSize: 13, marginLeft: 6, opacity: 0.7 }}>ℹ️</span>
              </TeamNotificationTooltip>
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '50%',
              right: 12,
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              outline: 'none',
              padding: 0,
              fontSize: '1.4rem',
              color: '#4b6360',
              lineHeight: 1,
            }}
            onClick={onClose}
            onMouseOver={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.07)'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
          >×</button>
        </div>
      </Modal.Header>
      
      <Modal.Body style={{ padding: '18px 23px' }}>
        {/* Current Plan Overview */}
        <Card className="mb-4" style={{ border: '1px solid #e3f2fd', background: '#f8fbff' }}>
          <Card.Body>
            <Row>
              <Col md={4}>
                <div className="text-center">
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#0d47a1' }}>
                    {currentSeats}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>Current Seats</div>
                </div>
              </Col>
              <Col md={4}>
                <div className="text-center">
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#2e7d32' }}>
                    ${currentMonthlyCost.toFixed(2)}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Monthly Cost</div>
                  <div style={{ color: '#888', fontSize: '0.8rem', marginTop: '4px' }}>
                    Base: ${(BASE_PLAN_PRICING[accountType] || BASE_PLAN_PRICING.solo)} + 
                    Additional Seats: ${(currentMonthlyCost - (BASE_PLAN_PRICING[accountType] || BASE_PLAN_PRICING.solo)).toFixed(2)}
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="text-center">
                  <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ed6c02' }}>
                    {maxAllowedSeats}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>Max Allowed</div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Seat Adjustment Controls */}
        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3" style={{ color: '#333', fontWeight: 600 }}>
              <FaCreditCard style={{ marginRight: 8, color: '#0d47a1' }} />
              Adjust Your Seats
            </h5>
            
            {/* Trial User Warning */}
            {(user?.accountType?.toLowerCase() === 'trial' || 
              user?.is_trial === 'true' || 
              user?.trial_status === 'active') && (
              <Alert variant="warning" className="mb-3">
                <FaInfoCircle className="me-2" />
                <strong>Trial Account Limitation:</strong> Seat changes unavailable on trial — upgrade to manage seats.
              </Alert>
            )}
            
            <div className="d-flex align-items-center justify-content-center mb-4" style={{ gap: '20px' }}>
              <Button
                variant="outline-primary"
                size="lg"
                disabled={(() => {
                  const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                     user?.is_trial === 'true' || 
                                     user?.trial_status === 'active';
                  return isTrialUser || targetSeats <= defaultSeats;
                })()}
                onClick={() => handleSeatChange(targetSeats - 1)}
                style={{ 
                  borderRadius: '50%', 
                  width: '50px', 
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: (() => {
                    const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                       user?.is_trial === 'true' || 
                                       user?.trial_status === 'active';
                    return (isTrialUser || targetSeats <= defaultSeats) ? 0.5 : 1;
                  })()
                }}
                title={(() => {
                  const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                     user?.is_trial === 'true' || 
                                     user?.trial_status === 'active';
                  if (isTrialUser) return 'Seat adjustments not available during trial period';
                  if (targetSeats <= defaultSeats) return 'Cannot reduce seats below base plan';
                  return 'Remove one seat';
                })()}
              >
                <FaMinus />
              </Button>
              
              <div className="text-center">
                <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#0d47a1', minWidth: '120px' }}>
                  {targetSeats}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>Target Seats</div>
              </div>
              
              <Button
                variant="outline-primary"
                size="lg"
                disabled={(() => {
                  const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                     user?.is_trial === 'true' || 
                                     user?.trial_status === 'active';
                  return isTrialUser || targetSeats >= maxAllowedSeats;
                })()}
                onClick={() => handleSeatChange(targetSeats + 1)}
                style={{ 
                  borderRadius: '50%', 
                  width: '50px', 
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: (() => {
                    const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                       user?.is_trial === 'true' || 
                                       user?.trial_status === 'active';
                    return (isTrialUser || targetSeats >= maxAllowedSeats) ? 0.5 : 1;
                  })()
                }}
                title={(() => {
                  const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                     user?.is_trial === 'true' || 
                                     user?.trial_status === 'active';
                  if (isTrialUser) return 'Seat adjustments not available on trial';
                  if (targetSeats >= maxAllowedSeats) return 'Cannot exceed maximum allowed seats';
                  return 'Add one seat';
                })()}
              >
                <FaPlus />
              </Button>
            </div>

            {/* Cost Preview */}
            {targetSeats !== currentSeats && (
              <Alert variant={costDifference > 0 ? 'warning' : 'success'} className="mb-3">
                <div className="d-flex align-items-center">
                  <FaInfoCircle style={{ marginRight: 8 }} />
                  <div>
                    <strong>Cost Change: </strong>
                    {costDifference > 0 ? '+' : ''}${costDifference.toFixed(2)}/month
                    <div style={{ fontSize: '0.9rem', marginTop: 4 }}>
                      New monthly cost: ${targetMonthlyCost.toFixed(2)}
                      {baseIncludedSeats > 0 && ` (${baseIncludedSeats} seats included, ${Math.max(0, targetSeats - baseIncludedSeats)} additional)`}
                    </div>
                  </div>
                </div>
              </Alert>
            )}
          </Card.Body>
        </Card>

        {/* Plan Information */}
        <Card style={{ border: '1px solid #e8f5e8', background: '#f9fdf9' }}>
          <Card.Body>
            <h6 style={{ color: '#2e7d32', fontWeight: 600, marginBottom: 12 }}>
              <FaCheck style={{ marginRight: 8 }} />
              Plan Details
            </h6>
            <Row>
              <Col md={6}>
                <div style={{ fontSize: '0.9rem', color: '#555' }}>
                  <div className="mb-2">
                    <strong>Default seats:</strong> {defaultSeats}
                  </div>
                  <div className="mb-2">
                    <strong>Cost per additional seat:</strong> ${monthlyCostPerSeat}/month
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div style={{ fontSize: '0.9rem', color: '#555' }}>
                  <div className="mb-2">
                    <strong>Maximum seats:</strong> {maxAllowedSeats}
                  </div>
                  <div className="mb-2">
                    <strong>Billing cycle:</strong> Monthly
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>


        {/* Success/Error Messages */}
        {success && (
          <Alert variant="success" className="mt-3">
            <FaCheck style={{ marginRight: 8 }} />
            {success}
          </Alert>
        )}
        
        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="d-flex justify-content-end mt-4" style={{ gap: '12px' }}>
          <Button
            style={{
              background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
              border: 'none',
              color: '#333',
              boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
              transition: 'all 0.2s ease-in-out',
              borderRadius: '4px',
              fontWeight: '500',
              padding: '8px 24px',
              fontSize: '1rem',
              letterSpacing: '0.5px'
            }}
            onClick={onClose}
          >
            Cancel
          </Button>
          
          <Button
            disabled={loading || targetSeats === currentSeats}
            onClick={updateSeats}
            style={{
              background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
              border: 'none',
              color: '#333',
              boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
              transition: 'all 0.2s ease-in-out',
              borderRadius: '4px',
              fontWeight: '500',
              padding: '8px 24px',
              fontSize: '1rem',
              letterSpacing: '0.5px'
            }}
          >
            {loading ? 'Saving...' : (
                  <>
                    <FaCheck className="me-2" />
                    Save
                  </>
                )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default Seats;